import {observer} from "mobx-react-lite";
import React, {useCallback, useEffect, useRef, useState} from 'react';
import classNames from "classnames";
import intl from "react-intl-universal";
import {BGAvatar} from "../Avatar/Avatar";
import {fileToBase64} from "../../../utils/file/fileToBase64";
import {removeFile} from "../../../utils/s3/removeFile";
import {CameraIcon} from "../icons/CameraIcon";
import {CloseIcon} from "../icons/CloseIcon";
import {logErrorToSentry} from "../../../utils/errors/logErrorToSentry";
import {openAvatarUploadContextMenu} from "../../elements/context-menu/AvatarUploadContextMenu/AvatarUploadContextMenu";
import {useAppState, useServiceRegistry, useUserState} from "@stores/AppStateContext";

import {useFileProvider} from "@stores/AppStateContext";
import {snackBarRef} from "@stores/snackbarRef";
import {MODALS} from "../../screens/modals/modal-names";
import {screenSizeProvider} from "@stores/ScreenSizeProvider";


import "./AvatarUpload.css";


const allowedTypes = ["image/png", "image/jpeg", "image/gif"];

export interface AvatarUploadProps {
    color: string;
    abbreviation: string;
    alt: string;
    fileKeyPart: string;
    className?: string;
    addImageHint?: string;
    replaceImageHint?: string;
    avatarPath?: string | null;
    size: 'small' | 'medium';
    removeAvatarCb: () => Promise<void>;
    saveAvatarPath: (s3FileName: string) => Promise<void>;
    isDark?: boolean;
    useContextMenu?: boolean;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = observer((
    {
        color,
        abbreviation,
        alt,
        fileKeyPart,
        size,
        avatarPath,
        addImageHint,
        replaceImageHint,
        removeAvatarCb,
        saveAvatarPath,
        className,
        isDark = false,
        useContextMenu = false,
    }
) => {
    const appState = useAppState();
    const fileProvider = useFileProvider();
    const serviceRegistry = useServiceRegistry();
    const storageService = serviceRegistry.getStorageService();

    const [avatarSrc, setAvatarSrc] = useState<string | undefined>(undefined);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const isMobile = screenSizeProvider.view !== 'desktop';
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (avatarPath && fileProvider) {
            fileProvider.getSignedUrl(avatarPath).then(src => {
                if (src) {
                    setAvatarSrc(src);
                }
            });
        } else {
            setAvatarSrc(undefined);
        }
    }, [avatarPath]);

    const openCropModal = (base64: string, fileType: string | null) => {
        appState.setModal({
            name: MODALS.CROP, params: {
                cropperBase: base64,
                cropperBaseType: fileType,
                fileKeyPart: fileKeyPart,
                saveAvatarPath: async (newAvatarPath: string) => {
                    await saveAvatarPath(newAvatarPath);
                    fileProvider?.getSignedUrl(newAvatarPath).then((signedUrl) => {
                        setAvatarSrc(signedUrl);
                    });
                }
            }
        });
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (isMobile && useContextMenu && e.isTrusted) {
            e.preventDefault();
            openAvatarUploadContextMenu(
                appState,
                fileKeyPart,
                {
                    onRemoveAvatar: removeAvatar,
                    hasAvatar: !!avatarPath,
                    fileKeyPart: fileKeyPart,
                    fileInputRef: fileInputRef,
                }, isDark);
        }
    };

    const onFileDrop = useCallback(
        (e: React.SyntheticEvent<EventTarget>) => {
            const target = e.target as HTMLInputElement;
            if ((target.files === null) || (target.files.length === 0)) return;
            if (allowedTypes.indexOf(String(target.files[0].type).toLowerCase()) === -1) {
                return;
            }

            const newFile = Object.values(target.files).map((file: File) => file);
            const file = newFile[0];
            fileToBase64(newFile[0]).then(base64 => {
                if (!base64.startsWith('data:image/')) {
                    throw new Error('Invalid base64 format');
                }
                openCropModal(base64, file.type || null);
            }).catch(() => {
                snackBarRef.current!.enqueueSnackbar({
                    variant: 'error',
                    message: intl.get('common.upload.error').d('Something went wrong while uploading the file, please contact us'),
                });
            });
            target.value = "";
        },
        [fileKeyPart, saveAvatarPath]
    );

    const removeAvatar = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        if (avatarPath) {
            removeFile(avatarPath, storageService, fileProvider!, appState.userState!.tenantId).catch(logErrorToSentry);
            removeAvatarCb().then(() => {
                setAvatarSrc(undefined);
            }).catch(logErrorToSentry);
        }
    };

    const onDragEnter = () => wrapperRef.current?.classList.add('dragover');
    const onDragLeave = () => wrapperRef.current?.classList.remove('dragover');

    return (
        <div
            className={classNames({
                'AvatarUpload__wrapper': true,
                'AvatarUpload--small': size === 'small',
                [className!]: className,
            })}
            ref={wrapperRef}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onFileDrop}
        >
            <div className={'AvatarUpload__hover'}/>
            <div className={'AvatarUpload__hover-elements'}>
                {!!avatarSrc && (
                    <CloseIcon
                        size={size === 'small' ? 14 : 16}
                        className={'theme-dark AvatarUpload__hover-icon AvatarUpload__hover-icon-close'}
                        onClick={removeAvatar}
                        style={{zIndex: 110}}
                    />
                )}
                <CameraIcon
                    size={size === 'small' ? 20 : 24}
                    className={'theme-dark AvatarUpload__hover-icon AvatarUpload__hover-icon-camera'}
                />
                {addImageHint && replaceImageHint && (
                    !!avatarSrc
                        ? <span className={"AvatarUpload__hover-text"}>{addImageHint}</span>
                        : <span className={"AvatarUpload__hover-text"}>{replaceImageHint}</span>
                )}
            </div>
            <BGAvatar
                src={avatarSrc}
                color={color}
                abbreviation={abbreviation}
                alt={alt}
                waitForSrc={!!avatarPath}
                size={'scale'}
                sx={{
                    width: '100%',
                    height: '100%',
                    fontWeight: 700,
                    fontSize: size === 'small' ? '14px' : '32px',
                    lineHeight: size === 'small' ? '20px' : '41px',
                    borderRadius: 6,
                }}
            />
            <input
                onClick={handleClick}
                type='file'
                name='avatar'
                ref={fileInputRef}
                onChange={onFileDrop}
                multiple={false}
                accept='image/jpg, image/png, image/jpeg'
                style={{
                    opacity: 0,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    zIndex: 100,
                }}
            />
        </div>
    );
});
